import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import Decimal from 'decimal.js';
import Core    from '@/core/Core';
import Numeric from '@/core/types/Numeric';
import Price   from '@/core/types/Price';
import Sum     from '@/core/types/Sum';
import type Unit from '@/domains/Units/Unit';

@Title_ru_RU_Class('Количество')
class Qty extends Numeric {
    static readonly SCALE = 3;

    static fromServer(value: string): Qty {
        return new Qty(value);
    }

    /** Результат вычисления — уже посчитанный Decimal. */
    static fromOperation(value: Decimal): Qty {
        return new Qty(value);
    }

    /** Количество + количество = количество. */
    plus(other: Qty | null | undefined): Qty {
        return Qty.fromOperation(this.value.plus(Qty.toDecimal(other)));
    }

    /** Количество − количество = количество. */
    minus(other: Qty | null | undefined): Qty {
        return Qty.fromOperation(this.value.minus(Qty.toDecimal(other)));
    }

    /** Кратно ли количество фасовке */
    isMultipleOf(packageVolume: Qty): boolean {
        return this.value.mod(packageVolume.value).isZero();
    }

    /** Ближайшее кратное фасовке вниз */
    floorToMultiple(packageVolume: Qty): Qty {
        return Qty.fromOperation(this.value.dividedBy(packageVolume.value).floor().times(packageVolume.value));
    }

    /** Ближайшее кратное фасовке вверх */
    ceilToMultiple(packageVolume: Qty): Qty {
        return Qty.fromOperation(this.value.dividedBy(packageVolume.value).ceil().times(packageVolume.value));
    }

    /** Часть количества в процентах: percentage(70) — 70% от него */
    percentage(percent: Decimal.Value): Qty {
        return Qty.fromOperation(this.value.times(percent).dividedBy(100));
    }

    /** Доля от целого в процентах — для подписей. От нулевого целого — 0 */
    percentOf(whole: Qty, precision: number = 2): number {
        let result: number;

        if (whole.isZero() === true) {
            result = 0;
        }
        else {
            result = this.value.times(100).dividedBy(whole.value).toDecimalPlaces(precision, Decimal.ROUND_HALF_UP).toNumber();
        }

        return result;
    }

    /**
     * Количество × цена = сумма.
     * Результат рождается типом Sum, который сам округлит до 2 знаков.
     */
    mul(price: Price | null | undefined): Sum {
        return Sum.fromOperation(this.value.times(Qty.toDecimal(price)));
    }

    /**
     * Человекочитаемое представление количества: число + единица измерения.
     * У количества нет валюты (в отличие от Sum/Price) — только единица.
     * Без хвостовых нулей ("10 шт", "12,5 кг") — то же, что formatSmart.
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     *
     * @param unit - единица измерения (имя клеится постфиксом; null — без единицы)
     */
    format(unit: Unit | null | undefined = null): string {
        return this.formatSmart(unit);
    }

    /**
     * Целочисленное представление количества: без дробной части и без
     * разделителей разрядов. Ходовой кейс — счётные сущности (паллетоместа,
     * штуки): «10 шт», «5 шт». Дробный хвост срезается округлением (страховка,
     * по смыслу такое количество уже целое). Единица клеится постфиксом
     * неразрывным пробелом, как в format; null — голое число.
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     *
     * @param unit - единица измерения (имя клеится постфиксом; null — без единицы)
     */
    formatAsInt(unit: Unit | null | undefined = null): string {
        if (this.isEmpty()) {
            return '';
        }

        let result = this.value.toFixed(0);

        if (Core.utils.isNullOrUndefined(unit) === false) {
            result += `\u00A0${unit.name}`;
        }

        return result;
    }

    /**
     * Количество с единицей измерения: "10 шт", "12,5 кг" — без хвостовых нулей.
     * Имя единицы клеится постфиксом неразрывным пробелом (\u00A0), чтобы число
     * и единица не разрывались по строкам. Зеркально Qty.format(unit).
     *
     * Пустое значение печатается пустой строкой, без единицы: единица без
     * числа смысла не имеет.
     *
     * @param unit - единица измерения; null — голое число
     */
    formatSmart(unit: Unit | null | undefined = null): string {
        let result = super.formatSmart();

        if (result !== '' && Core.utils.isNullOrUndefined(unit) === false) {
            result += `\u00A0${unit.name}`;
        }

        return result;
    }

    /**
     * Сокращённое количество с единицей измерения: "12,5 тыс шт", "3,45 млн кг".
     * Единица клеится постфиксом неразрывным пробелом — зеркально formatSmart.
     *
     * Пустое значение печатается пустой строкой, без единицы: единица без
     * числа смысла не имеет.
     *
     * @param unit - единица измерения; null — голое число
     */
    formatShorten(unit: Unit | null | undefined = null): string {
        let result = super.formatShorten();

        if (result !== '' && Core.utils.isNullOrUndefined(unit) === false) {
            result += `\u00A0${unit.name}`;
        }

        return result;
    }
}

export default Qty;